import React from 'react';
import clsx from 'clsx';
import { Home, Compass, Tag, Bookmark, MoreHorizontal } from 'lucide-react';

const NAV_ITEMS = [
  { key: 'home',    label: 'Home',    icon: Home },
  { key: 'explore', label: 'Explore', icon: Compass },
  { key: 'deals',   label: 'Deals',   icon: Tag },
  { key: 'saved',   label: 'Saved',   icon: Bookmark },
  { key: 'more',    label: 'More',    icon: MoreHorizontal },
];

/**
 * BottomNav — mobile-only sticky bottom navigation bar.
 *
 * Props:
 *   activeKey   string    — key of the currently active tab
 *   onSelect    fn(key)   — called when a tab is tapped
 *   items       array     — optional override of the default nav items
 *                           each item: { key, label, icon (Lucide component) }
 */
export function BottomNav({ activeKey, onSelect, items = NAV_ITEMS, className, style }) {
  return (
    <nav
      aria-label="Mobile navigation"
      className={clsx(
        // Only visible on mobile, hidden on md+
        'fixed bottom-0 left-0 right-0 z-50 flex md:hidden',
        className
      )}
      style={{
        backgroundColor: 'var(--primary, #0E5A2E)',
        borderTop: '1px solid color-mix(in srgb, var(--primary, #0E5A2E) 60%, black)',
        // Safe-area inset for notched phones
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        ...style,
      }}
    >
      {items.map(({ key, label, icon: Icon }) => {
        const isActive = key === activeKey;
        return (
          <button
            key={key}
            type="button"
            aria-label={label}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => onSelect?.(key)}
            className="flex flex-1 flex-col items-center justify-center gap-0.5 py-2 transition-opacity active:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-inset"
            style={{
              color: isActive
                ? 'var(--primary-foreground, #FFFFFF)'
                : 'color-mix(in srgb, var(--primary-foreground, #FFFFFF) 55%, var(--primary, #0E5A2E))',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <span
              className="flex items-center justify-center rounded-full px-3 py-0.5 transition-all"
              style={isActive ? {
                backgroundColor: 'color-mix(in srgb, var(--primary-foreground, #FFFFFF) 18%, transparent)',
              } : undefined}
            >
              <Icon
                size={20}
                strokeWidth={isActive ? 2.2 : 1.7}
                aria-hidden="true"
              />
            </span>
            <span
              className="text-[10px] font-semibold leading-none tracking-wide"
              style={{ fontFamily: 'var(--font-body, inherit)' }}
            >
              {label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

export default BottomNav;
